import { useState, useEffect } from 'react';
import { supabase, scheduleMedicineReminders } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

export const useMedicines = () => {
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [medicineHistory, setMedicineHistory] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchMedicines();
    }
  }, [user]);

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('medicines_mt2024')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setMedicines(data || []);
    } catch (error) {
      toast.error('Failed to fetch medicines');
      console.error('Error fetching medicines:', error);
    } finally {
      setLoading(false);
    }
  };

  const addMedicine = async (medicineData) => {
    try {
      // Ensure timing is properly formatted as an array
      const formattedData = {
        ...medicineData,
        user_id: user.id,
        timing: Array.isArray(medicineData.timing) ? medicineData.timing : [medicineData.timing],
        is_active: true,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('medicines_mt2024')
        .insert([formattedData])
        .select()
        .single();

      if (error) throw error;

      // Schedule reminders for this medicine
      try {
        await scheduleMedicineReminders(data);
      } catch (reminderError) {
        console.error('Error scheduling reminders:', reminderError);
        // Don't fail the medicine creation if reminders fail
      }

      setMedicines(prev => [data, ...prev]);
      toast.success('Medicine added successfully!');
      return data;
    } catch (error) {
      toast.error('Failed to add medicine');
      console.error('Error adding medicine:', error);
      throw error;
    }
  };

  const updateMedicine = async (id, updates) => {
    try {
      const { data, error } = await supabase
        .from('medicines_mt2024')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;

      setMedicines(prev => 
        prev.map(med => med.id === id ? data : med)
      );
      toast.success('Medicine updated successfully!');
      return data;
    } catch (error) {
      toast.error('Failed to update medicine');
      console.error('Error updating medicine:', error);
      throw error;
    }
  };

  const deleteMedicine = async (id) => {
    try {
      // Soft delete - mark as inactive
      const { error } = await supabase
        .from('medicines_mt2024')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;

      setMedicines(prev => prev.filter(med => med.id !== id));
      toast.success('Medicine deleted successfully!');
    } catch (error) {
      toast.error('Failed to delete medicine');
      console.error('Error deleting medicine:', error);
      throw error;
    }
  };

  const logMedicineIntake = async (medicineId, status = 'taken', notes = '') => {
    try {
      const currentTime = new Date();
      
      const { data, error } = await supabase
        .from('medicine_logs_mt2024')
        .insert([{
          medicine_id: medicineId,
          user_id: user.id,
          scheduled_time: currentTime.toISOString(),
          taken_time: status === 'taken' ? currentTime.toISOString() : null,
          status,
          notes,
          created_at: currentTime.toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      // Update medicine history
      await fetchMedicineHistory();

      const statusMessages = {
        taken: 'Medicine marked as taken! 💊',
        skipped: 'Dose marked as skipped',
        missed: 'Dose marked as missed'
      };

      toast.success(statusMessages[status] || 'Medicine status updated!');
      return data;
    } catch (error) {
      toast.error('Failed to log medicine intake');
      console.error('Error logging medicine intake:', error);
      throw error;
    }
  };

  const fetchMedicineHistory = async (medicineId = null, startDate = null, endDate = null) => {
    try {
      let query = supabase
        .from('medicine_logs_mt2024')
        .select(`
          *,
          medicines_mt2024 (
            name,
            dosage,
            type
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (medicineId) {
        query = query.eq('medicine_id', medicineId);
      }

      if (startDate) {
        query = query.gte('scheduled_time', startDate);
      }

      if (endDate) {
        query = query.lte('scheduled_time', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      setMedicineHistory(data || []);
      return data || [];
    } catch (error) {
      console.error('Error fetching medicine history:', error);
      throw error;
    }
  };

  const getMedicineStats = async (medicineId = null, days = 30) => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const history = await fetchMedicineHistory(medicineId, startDate.toISOString());
      
      const totalDoses = history.length;
      const takenDoses = history.filter(log => log.status === 'taken').length;
      const skippedDoses = history.filter(log => log.status === 'skipped').length;
      const missedDoses = history.filter(log => log.status === 'missed').length;
      
      const adherenceRate = totalDoses > 0 ? Math.round((takenDoses / totalDoses) * 100) : 0;

      return {
        totalDoses,
        takenDoses,
        skippedDoses,
        missedDoses,
        adherenceRate
      };
    } catch (error) {
      console.error('Error calculating medicine stats:', error);
      return {
        totalDoses: 0,
        takenDoses: 0,
        skippedDoses: 0,
        missedDoses: 0,
        adherenceRate: 0
      };
    }
  };

  const getTodaysDoses = () => {
    const today = new Date();
    return medicineHistory.filter(log => {
      const logDate = new Date(log.created_at);
      return logDate.toDateString() === today.toDateString();
    });
  };

  return {
    medicines,
    loading,
    medicineHistory,
    addMedicine,
    updateMedicine,
    deleteMedicine,
    logMedicineIntake,
    fetchMedicineHistory,
    getMedicineStats,
    getTodaysDoses,
    refetch: fetchMedicines,
  };
};